/* Composite the existing portrait in the reference pose; frame grain uses one cached theme asset. */
(() => {
  if (window.popCanvasRendererStarted) return;
  window.popCanvasRendererStarted = true;
  const selector = '[data-pop-ready-preview], .pop-reassurance-example__canvas, .cart-items__media-container.pop-canvas-mock, .cart-items__media-container.pop-frame-mock, [data-testid="product-information"].pop-reassurance-preview media-gallery .pop-pdp-primary-slide .product-media';
  const controllers = new Map();
  const pending = new Set();
  let frame = 0, scanFrame = 0;
  const frameTextureUrl = document.currentScript?.dataset.popFrameTexture || document.querySelector('script[data-pop-frame-texture]')?.dataset.popFrameTexture;
  const frameTexture = new Image();
  frameTexture.decoding = 'async';
  let frameTextureStarted = false;
  const loadFrameTexture = () => {
    if (frameTextureStarted || !frameTextureUrl) return;
    frameTextureStarted = true;
    frameTexture.addEventListener('load', () => controllers.forEach(schedule), { once: true });
    frameTexture.src = frameTextureUrl;
  };

  const ratioValue = (value) => {
    const parts = String(value || '').trim().split('/').map(Number);
    return parts.length === 2 && parts.every(n => n > 0) ? parts[0] / parts[1] : parts[0] > 0 ? parts[0] : 0;
  };
  const lerp = (a, b, t) => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
  const faceTransform = (width, height, corners, sourceHeight = height) => {
    const [p0, p1, p2, p3] = corners || [[.098,.034],[.951,.047],[.951,.959],[.088,.973]].map(([x,y]) => ({ x: x * width, y: y * height }));
    const dx1 = p1.x - p2.x, dx2 = p3.x - p2.x, dx3 = p0.x - p1.x + p2.x - p3.x;
    const dy1 = p1.y - p2.y, dy2 = p3.y - p2.y, dy3 = p0.y - p1.y + p2.y - p3.y;
    const determinant = dx1 * dy2 - dx2 * dy1;
    const g = (dx3 * dy2 - dx2 * dy3) / determinant, h = (dx1 * dy3 - dx3 * dy1) / determinant;
    const a = p1.x - p0.x + g * p1.x, b = p3.x - p0.x + h * p3.x;
    const d = p1.y - p0.y + g * p1.y, e = p3.y - p0.y + h * p3.y;
    return 'matrix3d(' + [a/width,d/width,0,g/width,b/sourceHeight,e/sourceHeight,0,h/sourceHeight,0,0,1,0,p0.x,p0.y,0,1].join(',') + ')';
  };
  const outline = (ctx, points, radius = 0) => {
    ctx.beginPath();
    points.forEach((p, i) => {
      const previous = points[(i + points.length - 1) % points.length], next = points[(i + 1) % points.length];
      const before = lerp(p, previous, radius / Math.max(1, Math.hypot(previous.x - p.x, previous.y - p.y)));
      const after = lerp(p, next, radius / Math.max(1, Math.hypot(next.x - p.x, next.y - p.y)));
      if (!i) ctx.moveTo(before.x, before.y); else ctx.lineTo(before.x, before.y);
      ctx.quadraticCurveTo(p.x, p.y, after.x, after.y);
    });
    ctx.closePath();
  };
  // Narrow affine strips give the face its small, fixed convergence at every display size.
  const drawQuad = (ctx, image, crop, points, mirrorX = false, steps = 64) => {
    ctx.save(); outline(ctx, points, .35); ctx.clip();
    const stripWidth = crop.width / steps;
    for (let i = 0; i < steps; i++) {
      const top = lerp(points[0], points[1], i / steps), next = lerp(points[0], points[1], (i + 1) / steps);
      const bottom = lerp(points[3], points[2], i / steps);
      const sx = crop.x + (mirrorX ? steps - i - 1 : i) * stripWidth;
      const overlap = Math.min(.65, Math.max(0, (image.naturalWidth || image.width) - sx - stripWidth));
      ctx.save();
      ctx.transform((next.x - top.x) / stripWidth, (next.y - top.y) / stripWidth, (bottom.x - top.x) / crop.height, (bottom.y - top.y) / crop.height, top.x, top.y);
      if (mirrorX) { ctx.translate(stripWidth, 0); ctx.scale(-1, 1); }
      ctx.drawImage(image, sx, crop.y, stripWidth + overlap, crop.height, 0, 0, stripWidth + overlap, crop.height);
      ctx.restore();
    }
    ctx.restore();
  };
  const drawCanvas = (ctx, image, width, height) => {
    const p = (x, y) => ({ x: width * x, y: height * y });
    // Front corners measured from the supplied 102 × 148 reference.
    const face = [p(.098, .034), p(.951, .047), p(.951, .959), p(.088, .973)];
    const side = [p(.046, .050), face[0], face[3], p(.041, .967)];
    const silhouette = [side[0], face[0], face[1], face[2], face[3], side[3]];
    const sourceWidth = image.naturalWidth || image.width, sourceHeight = image.naturalHeight || image.height;
    const crop = { x: 0, y: 0, width: sourceWidth, height: sourceHeight };
    const sourceRatio = crop.width / crop.height;
    // Match the native front's centered object-fit: cover crop, including the stage's pose allowance.
    const ratio = width / height;
    if (sourceRatio > ratio) { crop.width = crop.height * ratio; crop.x = (sourceWidth - crop.width) / 2; }
    else { crop.height = crop.width / ratio; crop.y = (sourceHeight - crop.height) / 2; }
    ctx.clearRect(0, 0, width, height);
    ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
    ctx.save(); outline(ctx, silhouette, width * .008);
    ctx.fillStyle = '#d4cbb9'; ctx.shadowColor = 'rgba(62,49,32,.24)';
    ctx.shadowBlur = width * .038; ctx.shadowOffsetX = -width * .011; ctx.shadowOffsetY = width * .014; ctx.fill();
    ctx.shadowBlur = width * .011; ctx.shadowOffsetX = -width * .004; ctx.shadowOffsetY = width * .004; ctx.shadowColor = 'rgba(49,39,25,.16)'; ctx.fill(); ctx.restore();

    // Mirroring the edge makes its front seam meet the exact front image pixels.
    drawQuad(ctx, image, { ...crop, width: Math.max(1, crop.width * .085) }, side, true, 16);
    ctx.save(); outline(ctx, side, width * .003); ctx.clip();
    const sideLight = ctx.createLinearGradient(width * .041, 0, width * .099, 0);
    sideLight.addColorStop(0, 'rgba(55,43,28,.25)'); sideLight.addColorStop(.45, 'rgba(58,47,31,.12)');
    sideLight.addColorStop(.90, 'rgba(51,40,24,.025)'); sideLight.addColorStop(1, 'rgba(255,248,229,.14)');
    ctx.fillStyle = sideLight; ctx.fillRect(0, 0, width, height); ctx.restore();

    // The front remains a native image, mapped by the matching homography for maximum detail.
    ctx.save(); ctx.lineCap = 'round'; ctx.lineWidth = Math.max(.25, width * .0015);
    ctx.strokeStyle = 'rgba(255,251,237,.15)'; ctx.beginPath(); ctx.moveTo(face[3].x, face[3].y); ctx.lineTo(face[0].x, face[0].y); ctx.lineTo(face[1].x, face[1].y); ctx.stroke();
    ctx.lineWidth = Math.max(.3, width * .0025); ctx.strokeStyle = 'rgba(49,37,24,.12)';
    ctx.beginPath(); ctx.moveTo(face[1].x, face[1].y); ctx.lineTo(face[2].x, face[2].y); ctx.lineTo(face[3].x, face[3].y); ctx.stroke(); ctx.restore();
  };
  const schedule = (controller) => {
    pending.add(controller);
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0; const work = Array.from(pending); pending.clear(); work.forEach(item => item.render());
    });
  };
  const drawFrame = (ctx, width, height, finish) => {
    const p = (x, y) => ({ x: width * x, y: height * y });
    const outer = [p(.100,.034),p(.951,.047),p(.951,.959),p(.090,.973)];
    const rail = width * .020;
    const inner = [
      {x:outer[0].x + rail,y:outer[0].y + rail}, {x:outer[1].x - rail,y:outer[1].y + rail},
      {x:outer[2].x - rail,y:outer[2].y - rail}, {x:outer[3].x + rail,y:outer[3].y - rail}
    ];
    const back = [p(.070,.045),outer[0],outer[3],p(.060,.969)];
    const silhouette = [back[0],outer[0],outer[1],outer[2],outer[3],back[3]];
    const base = {black:'#292a27',white:'#eeede8',natural:'#c2945f'}[finish];
    ctx.clearRect(0,0,width,height); ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
    ctx.save(); outline(ctx,silhouette,width * .005); ctx.fillStyle = base;
    ctx.shadowColor = 'rgba(45,35,25,.23)'; ctx.shadowBlur = width * .045;
    ctx.shadowOffsetX = -width * .012; ctx.shadowOffsetY = width * .018; ctx.fill(); ctx.restore();
    const material = (polygon, orientation) => {
      if (window.PopFrameMaterial?.draw) window.PopFrameMaterial.draw(ctx,polygon,finish,orientation,frameTexture);
      else { ctx.save(); outline(ctx,polygon); ctx.fillStyle = base; ctx.fill(); ctx.restore(); }
    };
    material(back,'side');
    ctx.save(); outline(ctx,back); ctx.clip();
    const sideShade = ctx.createLinearGradient(width*.060,0,width*.101,0);
    sideShade.addColorStop(0,'rgba(29,24,18,.25)'); sideShade.addColorStop(1,'rgba(29,24,18,.06)');
    ctx.fillStyle = sideShade; ctx.fillRect(0,0,width,height); ctx.restore();
    const rails = [
      [outer[0],outer[1],inner[1],inner[0]], [outer[1],outer[2],inner[2],inner[1]],
      [outer[2],outer[3],inner[3],inner[2]], [outer[3],outer[0],inner[0],inner[3]]
    ];
    rails.forEach((polygon,index) => {
      material(polygon,index%2 ? 'vertical' : 'horizontal');
      ctx.save(); outline(ctx,polygon); ctx.clip();
      ctx.fillStyle = index === 0 ? 'rgba(255,252,244,.08)' : index === 2 ? 'rgba(30,23,15,.075)' : 'rgba(30,23,15,.015)';
      ctx.fillRect(0,0,width,height); ctx.restore();
    });
    // The small inner rebate casts a shadow onto the paper, while the rails meet in mitres.
    ctx.save(); outline(ctx,inner); ctx.fillStyle = '#efeee9'; ctx.fill();
    ctx.lineWidth = Math.max(.3,width*.003); ctx.strokeStyle = finish === 'black' ? 'rgba(0,0,0,.58)' : 'rgba(46,35,24,.26)'; ctx.stroke();
    ctx.lineWidth = Math.max(.3,width*.002); ctx.strokeStyle = finish === 'black' ? 'rgba(0,0,0,.46)' : 'rgba(60,43,26,.23)';
    for (let i=0;i<4;i++) { ctx.beginPath(); ctx.moveTo(outer[i].x,outer[i].y); ctx.lineTo(inner[i].x,inner[i].y); ctx.stroke(); }
    ctx.lineWidth = Math.max(.3,width*.0018); ctx.strokeStyle = finish === 'black' ? 'rgba(255,255,255,.14)' : 'rgba(255,252,243,.36)';
    ctx.beginPath(); ctx.moveTo(outer[3].x,outer[3].y); ctx.lineTo(outer[0].x,outer[0].y); ctx.lineTo(outer[1].x,outer[1].y); ctx.stroke(); ctx.restore();
    const lip = width*.002;
    return [
      {x:inner[0].x+lip,y:inner[0].y+lip}, {x:inner[1].x-lip,y:inner[1].y+lip},
      {x:inner[2].x-lip,y:inner[2].y-lip}, {x:inner[3].x+lip,y:inner[3].y-lip}
    ];
  };
  class CanvasPortrait {
    constructor(stage) {
      this.stage = stage; this.image = stage.querySelector('img');
      this.sourceVersion = 0; this.disposed = false;
      this.canvas = document.createElement('canvas'); this.canvas.className = 'pop-canvas-render__output';
      this.canvas.setAttribute('aria-hidden', 'true'); stage.append(this.canvas);
      this.light = document.createElement('span'); this.light.className = 'pop-canvas-render__light';
      this.light.setAttribute('aria-hidden', 'true'); stage.append(this.light);
      this.onLoad = () => { this.resetSource(); schedule(this); };
      this.image?.addEventListener('load', this.onLoad); this.image?.addEventListener('error', this.onLoad);
      this.resize = new ResizeObserver(() => schedule(this)); this.resize.observe(stage);
      this.observer = new MutationObserver(mutations => {
        if (mutations.some(m => m.target === this.image && ['src', 'srcset', 'sizes'].includes(m.attributeName))) this.resetSource();
        schedule(this);
      });
      this.observer.observe(stage, { attributes: true, attributeFilter: ['class', 'style', 'data-finish'] });
      if (this.image) this.observer.observe(this.image, { attributes: true, attributeFilter: ['src', 'srcset', 'sizes', 'style'] });
      schedule(this);
    }
    resetSource() {
      this.sourceVersion++; this.lastRender = ''; this.raster = null;
      this.rasterKey = ''; this.rasterPending = ''; this.rasterFailed = '';
      if (this.stage.classList.contains('pop-canvas-rendered')) this.stage.classList.remove('pop-canvas-rendered');
    }
    imageKey() {
      const image = this.image;
      return [image.src, image.srcset, image.sizes, image.currentSrc, image.naturalWidth, image.naturalHeight, this.sourceVersion].join('|');
    }
    prepareRaster(key) {
      if (this.rasterKey === key && this.raster) return true;
      if (this.rasterPending === key || this.rasterFailed === key) return false;
      this.raster = null; this.rasterKey = ''; this.rasterPending = key;
      const {image, sourceVersion} = this;
      const current = () => !this.disposed && this.sourceVersion === sourceVersion && image.complete && this.imageKey() === key;
      const capture = () => {
        if (!current()) return;
        // Decode once, then sample a stable bitmap instead of narrow slices of the changing hero image.
        // The full-resolution front stays native; the edge raster is capped for mobile memory use.
        const raster = document.createElement('canvas');
        const scale = Math.min(1, 1536 / Math.max(image.naturalWidth, image.naturalHeight));
        raster.width = Math.max(1, Math.round(image.naturalWidth * scale));
        raster.height = Math.max(1, Math.round(image.naturalHeight * scale));
        const ctx = raster.getContext('2d');
        if (!ctx) throw new Error('Canvas context unavailable');
        ctx.drawImage(image, 0, 0, raster.width, raster.height);
        this.raster = raster; this.rasterKey = key; this.rasterPending = ''; this.lastRender = '';
        schedule(this);
      };
      Promise.resolve().then(() => {
        if (current() && typeof image.decode === 'function') return image.decode();
      }).then(capture).catch(() => {
        if (current()) { this.rasterPending = ''; this.rasterFailed = key; }
      });
      return false;
    }
    finish() {
      if (this.stage.classList.contains('product-media')) {
        const root = this.stage.closest('[data-testid="product-information"]');
        return root?.classList.contains('personalisation-ready') ? root.dataset.frameStyle : 'none';
      }
      const key = this.stage.dataset.finish || this.stage.dataset.popCartFinish;
      if (key) return ({'black-frame':'black','white-frame':'white',wood:'natural'})[key] || key;
      return this.stage.classList.contains('pop-canvas-mock') ? 'canvas' : 'none';
    }
    render() {
      if (this.disposed) return;
      const {stage, image, canvas} = this, finish = this.finish();
      const active = ['canvas','black','white','natural'].includes(finish);
      if (stage.classList.contains('pop-canvas-render-surface') !== active) stage.classList.toggle('pop-canvas-render-surface', active);
      if (!active || !image?.complete || image.naturalWidth < 8 || image.naturalHeight < 8) {
        if (stage.classList.contains('pop-canvas-rendered')) stage.classList.remove('pop-canvas-rendered');
        this.lastRender = ''; return;
      }
      const width = stage.clientWidth, height = stage.clientHeight;
      if (width < 8 || height < 8) return;
      const style = getComputedStyle(stage);
      const ratio = ratioValue(style.getPropertyValue('--pop-canvas-ratio')) || ratioValue(style.getPropertyValue('--pop-portrait-ratio')) || image.naturalWidth / image.naturalHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const imageKey = this.imageKey();
      if (finish === 'canvas' && !this.prepareRaster(imageKey)) {
        if (stage.classList.contains('pop-canvas-rendered')) stage.classList.remove('pop-canvas-rendered');
        this.lastRender = ''; return;
      }
      if (finish !== 'canvas') loadFrameTexture();
      const key = [imageKey, width, height, ratio, dpr, finish, frameTexture.naturalWidth || 0].join('|');
      if (this.lastRender === key) return;
      canvas.width = Math.round(width * dpr); canvas.height = Math.round(height * dpr);
      const ctx = canvas.getContext('2d'); if (!ctx) return; ctx.scale(dpr, dpr);
      try {
        let face;
        if (finish === 'canvas') drawCanvas(ctx, this.raster, width, height);
        else face = drawFrame(ctx, width, height, finish);
        const sourceHeight = finish === 'canvas' ? height : width / ratio;
        if (finish !== 'canvas' && stage.style.getPropertyValue('--pop-frame-source-height') !== sourceHeight + 'px') stage.style.setProperty('--pop-frame-source-height', sourceHeight + 'px');
        const transform = faceTransform(width, height, face, sourceHeight);
        if (stage.style.getPropertyValue('--pop-canvas-face-transform') !== transform) stage.style.setProperty('--pop-canvas-face-transform', transform);
        this.lastRender = key;
        if (!stage.classList.contains('pop-canvas-rendered')) stage.classList.add('pop-canvas-rendered');
      } catch (_error) {
        this.lastRender = '';
        if (stage.classList.contains('pop-canvas-rendered')) stage.classList.remove('pop-canvas-rendered');
      }
    }
    dispose() {
      this.disposed = true; this.raster = null; pending.delete(this);
      this.resize.disconnect(); this.observer.disconnect();
      this.image?.removeEventListener('load', this.onLoad); this.image?.removeEventListener('error', this.onLoad);
      this.canvas.remove(); this.light.remove();
      if (this.stage.classList.contains('pop-canvas-rendered')) this.stage.classList.remove('pop-canvas-rendered');
    }
  }
  const scan = () => {
    scanFrame = 0;
    controllers.forEach((controller, stage) => {
      if (!stage.isConnected || controller.image !== stage.querySelector('img') || !stage.contains(controller.canvas) || !stage.contains(controller.light)) {
        controller.dispose(); controllers.delete(stage);
      } else schedule(controller);
    });
    document.querySelectorAll(selector).forEach(stage => {
      if (!controllers.has(stage) && stage.querySelector('img')) controllers.set(stage, new CanvasPortrait(stage));
    });
  };
  const queueScan = () => { if (!scanFrame) scanFrame = requestAnimationFrame(scan); };
  const start = () => {
    scan();
    new MutationObserver(mutations => {
      if (mutations.some(m => m.type === 'childList'
        ? [...m.addedNodes, ...m.removedNodes].some(n => n.nodeType === 1 && !n.matches?.('.pop-canvas-render__output, .pop-canvas-render__light'))
        : m.target.matches?.('[data-testid="product-information"]'))) queueScan();
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-frame-style', 'class'] });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})();
